/**
 * <dasha-timeline> — the mahādaśās laid end to end on one time axis, with birth
 * and the present marked.
 *
 * SVG, so it prints and scales. Each block is exactly as wide as its period; the
 * text alternative is the tree beside it and a <title> on every block.
 *
 * Set `.tree`, `.offsetMinutes` (for the dates in the labels) and optionally
 * `.nowJD`.
 *
 * © Manish Jagdish Thatte
 */

import { el } from '../utils/svg.js';
import { formatJdLocal, formatYMD } from '../utils/format.js';
import { h, nz } from '../utils/dom.js';
import { periodLabel } from './dasha-tree.js';

const W = 1000;
const BAR_Y = 20;
const BAR_H = 44;

export class DashaTimeline extends HTMLElement {
  connectedCallback() { this.render(); }

  set tree(value) { this._tree = value; this.render(); }
  get tree() { return this._tree ?? null; }

  set offsetMinutes(value) { this._offset = value; if (this._tree) this.render(); }
  set nowJD(value) { this._now = value; if (this._tree) this.render(); }

  render() {
    this.textContent = '';
    const tree = this._tree;
    if (!tree) return;
    const offset = this._offset ?? 0;
    const t0 = tree.startJD, t1 = tree.endJD;
    const x = (jd) => ((jd - t0) / (t1 - t0)) * W;
    const year = (jd) => formatJdLocal(jd, offset, { seconds: false }).slice(0, 4);

    const svg = el('svg', {
      viewBox: `0 0 ${W} 100`,
      class: 'dasha-timeline__svg',
      role: 'img',
      'aria-label': `${tree.name} mahādaśās from ${year(t0)} to ${year(t1)}`
    });

    tree.roots.forEach((p, i) => {
      const x0 = x(p.startJD), w = x(p.endJD) - x0;
      const g = el('g', { class: `dasha-timeline__block${i % 2 ? ' is-alt' : ''}${p.beforeBirth ? ' is-before-birth' : ''}` });
      g.append(el('title', {}, `${periodLabel(p)}: ${formatJdLocal(p.startJD, offset, { seconds: false }).slice(0, 10)} → ` +
        `${formatJdLocal(p.endJD, offset, { seconds: false }).slice(0, 10)} (${formatYMD(p.days, { yearDays: tree.yearDays })})`));
      g.append(el('rect', { x: x0, y: BAR_Y, width: w, height: BAR_H }));
      // Show a name only where the block is wide enough to hold it.
      if (w > 46) g.append(el('text', { x: x0 + w / 2, y: BAR_Y + BAR_H / 2 + 5, 'text-anchor': 'middle' }, periodLabel(p).slice(0, 8)));
      svg.append(g);
    });

    // Year ticks at each decade of elapsed time from birth.
    const first = Math.ceil(Number(year(tree.birthJD)) / 10) * 10;
    for (let yr = first; ; yr += 10) {
      const jd = tree.birthJD + (yr - Number(year(tree.birthJD))) * tree.yearDays;
      if (jd > t1) break;
      if (jd < t0) continue;
      svg.append(el('line', { x1: x(jd), x2: x(jd), y1: BAR_Y + BAR_H, y2: BAR_Y + BAR_H + 6, class: 'dasha-timeline__tick' }));
      svg.append(el('text', { x: x(jd), y: BAR_Y + BAR_H + 20, 'text-anchor': 'middle', class: 'dasha-timeline__year' }, String(yr)));
    }

    const mark = (jd, cls, text) => {
      if (!(jd >= t0 && jd <= t1)) return;
      svg.append(el('line', { x1: x(jd), x2: x(jd), y1: BAR_Y - 8, y2: BAR_Y + BAR_H, class: cls }));
      svg.append(el('text', { x: x(jd), y: BAR_Y - 11, 'text-anchor': 'middle', class: 'dasha-timeline__mark' }, text));
    };
    mark(tree.birthJD, 'dasha-timeline__birth', 'birth');
    if (this._now != null) mark(this._now, 'dasha-timeline__now', 'now');

    this.append(h('div', { class: 'dasha-timeline' }, svg,
      h('p', { class: 'field-note' },
        nz(`Each block is one mahādaśā, drawn to scale over ${Math.round((t1 - t0) / tree.yearDays)} years. ` +
          'The first began before birth; only its balance at birth was lived.'))));
  }
}

customElements.define('dasha-timeline', DashaTimeline);
